<template>
    <section
        :id="`room-card-${roomKey}`"
        data-testid="room-guest-card"
        :data-room-key="roomKey"
        :data-room-index="roomIndex"
        class="rounded-xl border bg-white p-5 transition md:p-6"
        :class="cardClass"
    >
        <div class="flex flex-wrap items-start justify-between gap-3 border-b pb-4">
            <div class="flex min-w-0 items-start gap-3">
                <LazyImage
                    v-if="roomImage"
                    :src="roomImage"
                    :alt="roomTitle"
                    class="h-16 w-24 rounded-lg object-cover"
                />
                <div class="min-w-0">
                    <h3 class="text-base font-semibold text-gray-900 md:text-lg">
                        {{ $t("checkout.guest.room") }} {{ roomIndex + 1 }} - {{ roomTitle }}
                    </h3>
                    <p
                        v-if="propertyName"
                        class="mt-1 truncate text-xs text-gray-500 md:text-sm"
                    >
                        {{ propertyName }}
                    </p>
                    <div class="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-600">
                        <span
                            v-if="guestCapacity !== null && guestCapacity !== undefined"
                            class="inline-flex items-center gap-1"
                        >
                            <AppIcon icon="mdi:account" class="h-4 w-4" />
                            {{ guestCapacity }} {{ $t("checkout.guest.guests") }}
                        </span>
                        <span
                            v-if="roomSize"
                            class="inline-flex items-center gap-1"
                        >
                            <AppIcon
                                icon="mdi:arrow-expand-horizontal"
                                class="h-4 w-4"
                            />
                            {{ roomSize }}
                        </span>
                    </div>
                </div>
            </div>
            <RoomValidationBadge :state="visualState" />
        </div>

        <div class="mt-8 space-y-8">
            <AccordionSection
                data-testid="guest-section"
                data-section="guest"
                :title="$t('checkout.guest.informationTitle')"
                :summary="guestSummary"
                :helperText="$t('checkout.guest.helperGuest')"
                :expanded="activeSection === 'guest'"
                :isComplete="isGuestComplete"
                @toggle="toggleSection('guest')"
            >
                <GuestInformationSection
                    :form="form"
                    :errors="errors"
                    @update:form="emitFormUpdate"
                />
            </AccordionSection>

            <AccordionSection
                data-testid="guest-section"
                data-section="identity"
                :title="$t('checkout.guest.identityTitle')"
                :summary="identitySummary"
                :helperText="$t('checkout.guest.helperIdentity')"
                :expanded="activeSection === 'identity'"
                :isComplete="isIdentityComplete"
                @toggle="toggleSection('identity')"
            >
                <IdentityVerificationSection
                    :form="form"
                    :errors="errors"
                    @update:form="emitFormUpdate"
                />
            </AccordionSection>

            <AccordionSection
                data-testid="guest-section"
                data-section="emergency"
                :title="$t('checkout.guest.emergencyTitle')"
                :summary="emergencySummary"
                :helperText="$t('checkout.guest.helperEmergency')"
                :expanded="activeSection === 'emergency'"
                :isComplete="isEmergencyComplete"
                @toggle="toggleSection('emergency')"
            >
                <EmergencyContactSection
                    :form="form"
                    :errors="errors"
                    @update:form="emitFormUpdate"
                />
            </AccordionSection>
        </div>

        <div
            v-if="showSaveAction"
            class="mt-8 flex flex-wrap items-center justify-between gap-3 border-t pt-4"
        >
            <p class="text-xs text-gray-500">{{ saveCaption }}</p>
            <button
                type="button"
                class="rounded-lg px-4 py-2 text-sm font-semibold text-white"
                :class="saveBtnClass"
                :disabled="isSaving"
                @click="$emit('save')"
            >
                {{ $t("checkout.guest.save") }}
            </button>
        </div>
    </section>
</template>

<script>
import AccordionSection from "@/components/modules/AccordionSection.vue";
import GuestInformationSection from "@/components/modules/GuestInformationSection.vue";
import IdentityVerificationSection from "@/components/modules/IdentityVerificationSection.vue";
import EmergencyContactSection from "@/components/modules/EmergencyContactSection.vue";
import RoomValidationBadge from "@/components/modules/RoomValidationBadge.vue";
import LazyImage from "@/components/modules/LazyImage.vue";

// Error key (dari GuestDataSection.validateForm) -> [section accordion, data-testid field]
const FIELD_TARGET = {
    guestName: ["guest", "guest-name"],
    email: ["guest", "guest-email"],
    phone: ["guest", "guest-phone"],
    identityType: ["identity", "guest-identity-type"],
    identityNumber: ["identity", "guest-identity-number"],
    identityUpload: ["identity", "guest-identity-upload"],
    emergencyName: ["emergency", "emergency-name"],
    emergencyPhone: ["emergency", "emergency-phone"],
    emergencyRelation: ["emergency", "emergency-relation"],
    emergencyRelationOther: ["emergency", "emergency-relation-other"],
};

export default {
    name: "RoomGuestCard",
    components: {
        AccordionSection,
        GuestInformationSection,
        IdentityVerificationSection,
        EmergencyContactSection,
        RoomValidationBadge,
        LazyImage,
    },
    props: {
        roomKey: {
            type: [String, Number],
            required: true,
        },
        roomIndex: {
            type: Number,
            required: true,
        },
        roomTitle: {
            type: String,
            default: "",
        },
        propertyName: {
            type: String,
            default: "",
        },
        guestCapacity: {
            type: [String, Number],
            default: null,
        },
        roomSize: {
            type: String,
            default: "",
        },
        roomImage: {
            type: String,
            default: "",
        },
        form: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
        visualState: {
            type: String,
            default: "incomplete",
        },
        lastSavedLabel: {
            type: String,
            default: null,
        },
        showSaveAction: {
            type: Boolean,
            default: true,
        },
        isSaving: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            activeSection: "guest",
        };
    },
    computed: {
        cardClass() {
            const map = {
                complete: "border-green-300",
                incomplete: "border-gray-200",
                error: "border-red-300",
                uploading: "border-blue-300",
                saved: "border-emerald-300",
            };
            return map[this.visualState] || "border-gray-200";
        },
        saveBtnClass() {
            if (this.isSaving) return "bg-blue-400 cursor-not-allowed";
            if (this.visualState === "error") return "bg-red-500 hover:bg-red-600";
            if (this.visualState === "uploading") return "bg-blue-400";
            return "bg-blue-500 hover:bg-blue-600";
        },
        saveCaption() {
            if (this.visualState === "saved")
                return this.$t("checkout.guest.saved", {
                    time: this.lastSavedLabel || this.$t("checkout.guest.notSaved"),
                });
            if (this.visualState === "uploading")
                return this.$t("checkout.guest.uploadRunning");
            if (this.visualState === "error")
                return this.$t("checkout.guest.invalidData");
            if (this.visualState === "complete")
                return this.$t("checkout.guest.valid");
            return this.$t("checkout.guest.fillThenSave");
        },
        guestSummary() {
            const parts = [
                (this.form.guestName || "").trim(),
                (this.form.email || "").trim(),
                (this.form.phone || "").trim(),
            ].filter(Boolean);
            return parts.length
                ? parts.join(" · ")
                : this.$t("checkout.guest.notFilled");
        },
        identitySummary() {
            if (this.form.identityUpload?.uploadState === "success") {
                const type = (
                    this.form.identityType || this.$t("checkout.identity.defaultName")
                ).trim();
                return this.$t("checkout.guest.uploadedSuffix", { type });
            }
            if ((this.form.identityType || "").trim() || (this.form.identityNumber || "").trim()) {
                const parts = [
                    (this.form.identityType || "").trim(),
                    (this.form.identityNumber || "").trim(),
                ].filter(Boolean);
                return parts.join(" · ");
            }
            return this.$t("checkout.guest.notFilled");
        },
        emergencySummary() {
            const name = (this.form.emergency?.name || "").trim();
            const phone = (this.form.emergency?.phone || "").trim();
            if (!name && !phone) return this.$t("checkout.guest.notFilled");
            return [name, phone].filter(Boolean).join(" · ");
        },
        isGuestComplete() {
            return Boolean(
                (this.form.guestName || "").trim() &&
                    (this.form.email || "").trim() &&
                    (this.form.phone || "").trim()
            );
        },
        isIdentityComplete() {
            return Boolean(
                (this.form.identityType || "").trim() &&
                    (this.form.identityNumber || "").trim() &&
                    this.form.identityUpload?.uploadState === "success"
            );
        },
        isEmergencyComplete() {
            const emergency = this.form.emergency || {};
            const isOtherValid =
                emergency.relation !== "OTHER" ||
                Boolean((emergency.relationOther || "").trim());
            return Boolean(
                (emergency.name || "").trim() &&
                    (emergency.phone || "").trim() &&
                    (emergency.relation || "").trim() &&
                    isOtherValid
            );
        },
    },
    methods: {
        emitFormUpdate(nextForm) {
            this.$emit("update:form", nextForm);
        },
        // Buka section yang memuat field bermasalah, lalu scroll + focus ke field-nya.
        focusError(fieldKey) {
            const [section, testid] = FIELD_TARGET[fieldKey] || [];
            if (section) this.activeSection = section;
            this.$nextTick(() => {
                const holder =
                    (testid && this.$el.querySelector(`[data-testid="${testid}"]`)) ||
                    this.$el;
                const candidate = holder.matches("input, select, textarea")
                    ? holder
                    : holder.querySelector('input[type="tel"]') ||
                      holder.querySelector("input, select, textarea");
                // Input tersembunyi (mis. file picker upload) tak bisa di-scroll/focus:
                // jatuh ke pembungkusnya yang memang terlihat.
                const field =
                    candidate && candidate.offsetParent !== null ? candidate : null;
                (field || holder).scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                });
                field?.focus({ preventScroll: true });
            });
        },
        toggleSection(sectionKey) {
            this.activeSection =
                this.activeSection === sectionKey ? null : sectionKey;
        },
    },
};
</script>
